import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Select,
  Tooltip,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useCurrencies from "../components/useCurrencies.js";
import useTimeZones from "../components/useTimeZones.js";
import useLanguages from "../components/useLanguages.js";
import SiteNavbar from "../components/SiteNavbar.js";
import config from "../config";
import countries from "../data/countries";

const baseUrl = config.baseURL;

// ── Brand tokens (matched to Spade ERP landing page) ─────────────────────────
const T = {
  dark: "#2E4053",
  darkDeep: "#1C2A3D",
  muted: "#34495E",
  border: "#A6AAAD",
  surface: "#F4F6F9",
  surfaceAlt: "#D1D8E0",
  chipText: "#D1D8E0",
  white: "#FFFFFF",
};

const primaryButtonStyles = {
  backgroundColor: T.dark,
  color: T.white,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  padding: "10px",
  fontSize: 15,

  boxShadow: "0 4px 16px rgba(46,64,83,.2)",
  transition: "transform .15s, background-color .15s",
  "&:hover": {
    backgroundColor: T.darkDeep,
    transform: "scale(1.01)",
  },
};

// Shared field style — rounded, white bg, dark focus ring
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: T.white,
    "& fieldset": { borderColor: T.surfaceAlt },
    "&:hover fieldset": { borderColor: T.border },
    "&.Mui-focused fieldset": { borderColor: T.dark },
  },
  "& .MuiInputLabel-root": { color: T.muted },
  "& .MuiInputLabel-root.Mui-focused": { color: T.dark },
};

const MAX_DESCRIPTION_LENGTH = 500;
const MAX_FILE_SIZE_MB = 10;

const companyTypes = [
  "Sole Proprietorship",
  "Partnership",
  "Corporation",
  "Non-Profit",
  "Other",
];
const businessCategories = [
  "SaaS",
  "E-commerce",
  "FinTech",
  "Healthcare Services",
  "Manufacturing",
  "Education",
  "Retail",
  "Logistics",
  "Marketing",
  "Consulting",
  "Non-profit",
  "Other",
];
const businessIndustries = [
  "Technology",
  "Finance",
  "Healthcare",
  "Education",
  "Manufacturing",
  "Retail",
  "Transportation",
  "Media & Entertainment",
  "Hospitality",
  "Agriculture",
  "Energy & Utilities",
  "Construction",
  "Real Estate",
  "Legal Services",
  "Automotive",
  "Telecommunications",
  "Food & Beverage",
  "Travel & Tourism",
  "Government",
  "Non-profit",
  "Other",
];

const Register = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState("register");
  const [userDetails, setUserDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    logo: null,
    companyType: "",
    websiteUrl: "",
    role: "admin",
    description: "",
    business_category: "",
    business_industry: "",
    tagline: "",
    companySize: "",
    currency: "",
    timeZone: "",
    language: "",
  });
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    expiryDate: "",
    cardHolderName: "",
    cvc: "",
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    companyType: "",
    websiteUrl: "",
    companySize: "",
    business_category: "",
    business_industry: "",
    currency: "",
    timeZone: "",
    language: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currencies: availableCurrencies } = useCurrencies();
  const { timeZones } = useTimeZones();
  const { languages } = useLanguages();
  const steps = ["User Information", "Company Information"];
  const queryParams = new URLSearchParams(location.search);
  const fromPricing = queryParams.get("fromPricing");
  const planId = queryParams.get("planId");
  const [resendToken, setResendToken] = useState(null);

  const handleChange = (event, step) => {
    const { name, value, files } = event.target;
    if (step === 0) {
      setUserDetails({ ...userDetails, [name]: value });
    } else if (step === 1) {
      if (name === "logo") {
        if (files && files[0]) {
          const file = files[0];
          const fileSizeMB = file.size / (1024 * 1024);
          if (fileSizeMB > MAX_FILE_SIZE_MB) {
            toast.error(
              `File size exceeds ${MAX_FILE_SIZE_MB} MB. Please choose a smaller image.`
            );
            event.target.value = "";
            setUserDetails({ ...userDetails, logo: null, logoPreview: null });
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            setUserDetails({
              ...userDetails,
              logo: file,
              logoPreview: reader.result,
            });
          };
          reader.readAsDataURL(file);
        }
      } else {
        setUserDetails({ ...userDetails, [name]: value });
      }
    }
  };

  const handleNext = () => {
    const newErrors = { ...errors };
    let isValid = true;

    if (activeStep === 0) {
      if (!userDetails.firstName) {
        newErrors.firstName = "First name is required";
        isValid = false;
        toast.error("First name is required");
      } else {
        newErrors.firstName = "";
      }
      if (!userDetails.lastName) {
        newErrors.lastName = "Last name is required";
        isValid = false;
        toast.error("Last name is required");
      } else {
        newErrors.lastName = "";
      }
      if (!userDetails.email) {
        newErrors.email = "Email is required";
        isValid = false;
        toast.error("Email is required");
      } else if (!isValidEmail(userDetails.email)) {
        newErrors.email = "Email is not in the correct format";
        isValid = false;
        toast.error("Email is not in the correct format");
      } else {
        newErrors.email = "";
      }
      if (!userDetails.phone) {
        newErrors.phone = "Phone number is required";
        isValid = false;
        toast.error("Phone number is required");
      } else if (!isValidPhone(userDetails.phone)) {
        newErrors.phone =
          "Phone number is not valid (must include international code)";
        isValid = false;
        toast.error("Phone number is not valid");
      } else {
        newErrors.phone = "";
      }
      if (!userDetails.countryCode) {
        newErrors.countryCode = "Country code is required";
        isValid = false;
        toast.error("Country code is required");
      } else if (!/^\+[0-9]{1,4}$/.test(userDetails.countryCode)) {
        newErrors.countryCode = "Invalid country code format (e.g. +20, +966)";
        isValid = false;
        toast.error("Invalid country code format");
      } else {
        newErrors.countryCode = "";
      }
      if (!userDetails.password) {
        newErrors.password = "Password is required";
        isValid = false;
        toast.error("Password is required");
      } else if (!isValidPassword(userDetails.password)) {
        newErrors.password =
          "Password must be at least 8 characters long and contain a number, uppercase, lowercase letter, and at least one special character";
        isValid = false;
        toast.error("Password is not strong enough");
      } else {
        newErrors.password = "";
      }
      if (!userDetails.confirmPassword) {
        newErrors.confirmPassword = "Confirm password is required";
        isValid = false;
        toast.error("Confirm password is required");
      } else if (userDetails.password !== userDetails.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
        isValid = false;
        toast.error("Passwords do not match");
      } else {
        newErrors.confirmPassword = "";
      }
    } else if (activeStep === 1) {
      if (!userDetails.companyName) {
        newErrors.companyName = "Company name is required";
        isValid = false;
        toast.error("Company name is required");
      } else {
        newErrors.companyName = "";
      }
      if (!userDetails.companyType) {
        newErrors.companyType = "Company type is required";
        isValid = false;
        toast.error("Company type is required");
      } else {
        newErrors.companyType = "";
      }
      if (!userDetails.description) {
        newErrors.description = "Description is required";
        isValid = false;
        toast.error("Description is required");
      } else {
        newErrors.description = "";
      }
      if (!userDetails.currency) {
        newErrors.currency = "Currency is required";
        isValid = false;
        toast.error("Currency is required");
      } else {
        newErrors.currency = "";
      }
      if (!userDetails.business_category) {
        newErrors.business_category = "Business category is required";
        isValid = false;
        toast.error("Business category is required");
      } else {
        newErrors.business_category = "";
      }
      if (!userDetails.business_industry) {
        newErrors.business_industry = "Business industry is required";
        isValid = false;
        toast.error("Business industry is required");
      } else {
        newErrors.business_industry = "";
      }
      if (!userDetails.timeZone) {
        newErrors.timeZone = "Time Zone is required";
        isValid = false;
        toast.error("Time Zone is required");
      } else {
        newErrors.timeZone = "";
      }
      if (!userDetails.language) {
        newErrors.language = "Language is required";
        isValid = false;
        toast.error("Language is required");
      } else {
        newErrors.language = "";
      }
      if (!userDetails.websiteUrl) {
        newErrors.websiteUrl = "Website URL is required";
        isValid = false;
        toast.error("Website URL is required");
      } else if (!isValidURL(userDetails.websiteUrl)) {
        newErrors.websiteUrl = "Website URL is not valid";
        isValid = false;
        toast.error("Website URL is not valid");
      } else {
        newErrors.websiteUrl = "";
      }
      if (!userDetails.companySize) {
        newErrors.companySize = "Company size is required";
        isValid = false;
        toast.error("Company size is required");
      } else if (
        isNaN(userDetails.companySize) ||
        userDetails.companySize < 1 ||
        userDetails.companySize > 10000
      ) {
        newErrors.companySize =
          "Company size must be a number between 1 and 10,000";
        isValid = false;
        toast.error("Company size is invalid");
      } else {
        newErrors.companySize = "";
      }
    }

    setErrors(newErrors);
    if (isValid) {
      setActiveStep(activeStep + 1);
    } else {
      toast.error("Please fill in all required fields correctly.");
    }
  };

  const isValidEmail = (email) =>
    /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(email);
  const isValidPhone = (phone) => /^[0-9]{10,15}$/.test(phone);
  const isValidPassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
      password
    );
  const isValidURL = (url) =>
    /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z0-9]{2,4}(\/[a-z0-9-_.,?=&%#]*)*$/i.test(
      url
    );

  const handleBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const handleCountryChange = (event, country) => {
    if (country)
      setUserDetails((prev) => ({ ...prev, countryCode: country.dialCode }));
    setErrors((prev) => ({ ...prev, countryCode: "" }));
  };

  const handleCurrencyChange = (event, value) => {
    if (value) {
      setUserDetails((prev) => ({ ...prev, currency: value.code }));
      setErrors((prev) => ({ ...prev, currency: "" }));
    }
  };

  const handlePhoneChange = (e) =>
    setUserDetails({ ...userDetails, phone: e.target.value });

  const handleSubmit = async () => {
    const newErrors = { ...errors };
    let isValid = true;
    Object.keys(userDetails).forEach((key) => {
      if (!userDetails[key]) {
        newErrors[key] = `${key} is required`;
        isValid = false;
        toast.error(`${key} is required`);
      } else {
        newErrors[key] = "";
      }
    });
    if (userDetails.password !== userDetails.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
      toast.error("Passwords do not match");
    }
    setErrors(newErrors);
    if (!isValid) return;

    const finalData = {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      email: userDetails.email,
      password: userDetails.password,
      countryCode: userDetails.countryCode,
      phone: userDetails.phone,
      companyName: userDetails.companyName,
      logo: userDetails.logo,
      companyType: userDetails.companyType,
      websiteUrl: userDetails.websiteUrl,
      role: userDetails.role,
      description: userDetails.description,
      business_category: userDetails.business_category,
      business_industry: userDetails.business_industry,
      tagline: userDetails.tagline,
      companySize: userDetails.companySize,
      currency: userDetails.currency,
      timeZone: userDetails.timeZone,
      language: userDetails.language,
    };

    try {
      setLoading(true);
      const formData = new FormData();
      for (const [key, value] of Object.entries(finalData)) {
        if (key === "logo" && value) formData.append(key, value);
        else formData.append(key, value);
      }
      const response = await axios.post(`${baseUrl}/auth/register`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLoading(false);
      const messageId = response?.data?.messageId;
      switch (messageId) {
        case 0:
          toast.success(
            "Account created successfully! Please verify your email."
          );
          const token = response.data.resendToken;
          if (token) setResendToken(token);
          navigate("/RegistrationSuccess", { state: { resendToken: token } });
          break;
        case 1:
          toast.error("Email already exists. Please use a different email.");
          break;
        case 2:
          toast.error("Failed to send verification email. Try again later.");
          break;
        case 3:
          toast.error(
            "File size exceeds 10 MB. Please choose a smaller image."
          );
          break;
        case 4:
          toast.error(
            "Image dimensions exceed 5000x5000px. Please upload a smaller image."
          );
          break;
        default:
          toast.error("An unknown error occurred. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Response:", error?.response?.data || error.message);
    }
  };

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ "& .MuiTextField-root": { mb: 3 } }}>
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{ color: T.dark, fontWeight: 700, mb: 2 }}
          >
            Personal Details
          </Typography>
          <TextField
            label="First Name"
            variant="outlined"
            name="firstName"
            value={userDetails.firstName}
            onChange={(e) => handleChange(e, 0)}
            fullWidth
            required
            error={!!errors.firstName}
            helperText={errors.firstName}
            sx={fieldSx}
          />
          <TextField
            label="Last Name"
            variant="outlined"
            name="lastName"
            value={userDetails.lastName}
            onChange={(e) => handleChange(e, 0)}
            fullWidth
            required
            error={!!errors.lastName}
            helperText={errors.lastName}
            sx={fieldSx}
          />
          <TextField
            label="Email Address"
            variant="outlined"
            name="email"
            type="email"
            value={userDetails.email}
            onChange={(e) => handleChange(e, 0)}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email || "We'll send a verification link here."}
            sx={fieldSx}
          />
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
          >
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => `${option.name} (${option.dialCode})`}
              value={
                countries.find((c) => c.dialCode === userDetails.countryCode) ||
                null
              }
              onChange={handleCountryChange}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography component="span" fontSize="20px">
                    {option.flag}
                  </Typography>
                  <Typography component="span" sx={{ flex: 1 }}>
                    {option.name}
                  </Typography>
                  <Typography component="span" color="text.secondary">
                    {option.dialCode}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  variant="outlined"
                  required
                  error={!!errors.countryCode}
                  helperText={errors.countryCode}
                  sx={{ width: { xs: "100%", sm: "200px" }, ...fieldSx }}
                />
              )}
            />
            <TextField
              label="Phone Number"
              variant="outlined"
              name="phone"
              type="tel"
              value={userDetails.phone || ""}
              onChange={handlePhoneChange}
              inputProps={{ maxLength: 15 }}
              required
              error={!!errors.phone}
              helperText={errors.phone || "Enter without country code prefix."}
              sx={{ flex: 1, ...fieldSx }}
            />
          </Box>
          <TextField
            label="Password"
            variant="outlined"
            type="password"
            name="password"
            value={userDetails.password}
            onChange={(e) => handleChange(e, 0)}
            fullWidth
            required
            error={!!errors.password}
            helperText={
              errors.password ||
              "Min 8 chars, 1 upper, 1 lower, 1 number, 1 symbol."
            }
            sx={fieldSx}
          />
          <TextField
            label="Confirm Password"
            variant="outlined"
            type="password"
            name="confirmPassword"
            value={userDetails.confirmPassword}
            onChange={(e) => handleChange(e, 0)}
            fullWidth
            required
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            sx={fieldSx}
          />
          <Button
            type="button"
            variant="contained"
            sx={{ mt: 2, ...primaryButtonStyles }}
            onClick={handleNext}
            fullWidth
          >
            Continue to Company Info
          </Button>
        </Box>
      );
    } else if (activeStep === 1) {
      return (
        <Box
          sx={{
            "& .MuiTextField-root": { mb: 3 },
            "& .MuiFormControl-root": { mb: 3 },
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{ color: T.dark, fontWeight: 700, mb: 2 }}
          >
            Company Details
          </Typography>
          <TextField
            label="Company Name"
            variant="outlined"
            name="companyName"
            value={userDetails.companyName}
            onChange={(e) => handleChange(e, 1)}
            fullWidth
            required
            error={!!errors.companyName}
            helperText={errors.companyName}
            sx={fieldSx}
          />
          <TextField
            label="Tagline (Optional)"
            variant="outlined"
            name="tagline"
            value={userDetails.tagline}
            onChange={(e) => handleChange(e, 1)}
            fullWidth
            sx={{ mb: 3, ...fieldSx }}
          />

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
          >
            <FormControl
              fullWidth
              required
              error={!!errors.companyType}
              sx={fieldSx}
            >
              <InputLabel>Company Type</InputLabel>
              <Select
                label="Company Type"
                name="companyType"
                value={userDetails.companyType}
                onChange={(e) => handleChange(e, 1)}
                sx={{ borderRadius: "10px" }}
              >
                {companyTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {errors.companyType && (
                <Typography color="error" variant="caption">
                  {errors.companyType}
                </Typography>
              )}
            </FormControl>
            <Autocomplete
              options={availableCurrencies}
              getOptionLabel={(option) => `${option.code} `}
              value={
                availableCurrencies.find(
                  (c) => c.code === userDetails.currency
                ) || null
              }
              onChange={handleCurrencyChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Primary Currency"
                  variant="outlined"
                  required
                  error={!!errors.currency}
                  helperText={errors.currency}
                  sx={{ width: { xs: "100%", sm: "220px" }, ...fieldSx }}
                />
              )}
              sx={{ width: { xs: "100%", sm: "50%" } }}
            />
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
          >
            <Tooltip
              title="Industries represent broader economic sectors where the business operates."
              arrow
            >
              <FormControl
                fullWidth
                required
                error={!!errors.business_industry}
                sx={fieldSx}
              >
                <InputLabel>Business Industry</InputLabel>
                <Select
                  label="Business Industry"
                  name="business_industry"
                  value={userDetails.business_industry}
                  onChange={(e) => handleChange(e, 1)}
                  sx={{ borderRadius: "10px" }}
                >
                  {businessIndustries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
                {errors.business_industry && (
                  <Typography color="error" variant="caption">
                    {errors.business_industry}
                  </Typography>
                )}
              </FormControl>
            </Tooltip>
            <Tooltip
              title="Categories focus on the type of business model or service offered."
              arrow
            >
              <FormControl
                fullWidth
                required
                error={!!errors.business_category}
                sx={fieldSx}
              >
                <InputLabel>Business Category</InputLabel>
                <Select
                  label="Business Category"
                  name="business_category"
                  value={userDetails.business_category}
                  onChange={(e) => handleChange(e, 1)}
                  sx={{ borderRadius: "10px" }}
                >
                  {businessCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
                {errors.business_category && (
                  <Typography color="error" variant="caption">
                    {errors.business_category}
                  </Typography>
                )}
              </FormControl>
            </Tooltip>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
          >
            <FormControl fullWidth required sx={fieldSx}>
              <InputLabel>Time Zone</InputLabel>
              <Select
                label="Time Zone"
                name="timeZone"
                value={userDetails.timeZone}
                onChange={(e) => handleChange(e, 1)}
                sx={{ borderRadius: "10px" }}
              >
                {timeZones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
              {errors.timeZone && (
                <Typography color="error" variant="caption">
                  {errors.timeZone}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth required sx={fieldSx}>
              <InputLabel>Language</InputLabel>
              <Select
                label="Language"
                name="language"
                value={userDetails.language}
                onChange={(e) => handleChange(e, 1)}
                sx={{ borderRadius: "10px" }}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
              </Select>
              {errors.language && (
                <Typography color="error" variant="caption">
                  {errors.language}
                </Typography>
              )}
            </FormControl>
          </Box>

          <TextField
            label="Company Size (Employees)"
            variant="outlined"
            name="companySize"
            type="number"
            value={userDetails.companySize}
            onChange={(e) => handleChange(e, 1)}
            fullWidth
            required
            inputProps={{ min: 1, max: 10000, step: 1 }}
            error={!!errors.companySize}
            helperText={errors.companySize || "Number of employees (1-10,000)."}
            sx={fieldSx}
          />
          <TextField
            label="Website URL"
            variant="outlined"
            name="websiteUrl"
            type="url"
            value={userDetails.websiteUrl}
            onChange={(e) => handleChange(e, 1)}
            fullWidth
            required
            error={!!errors.websiteUrl}
            helperText={
              errors.websiteUrl || "e.g., https://www.yourcompany.com"
            }
            sx={fieldSx}
          />
          <TextField
            label="Company Description"
            variant="outlined"
            name="description"
            value={userDetails.description}
            onChange={(e) => handleChange(e, 1)}
            fullWidth
            multiline
            rows={3}
            required
            error={!!errors.description}
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
            helperText={
              errors.description ||
              `Briefly describe your company's mission or service. (${userDetails.description.length} / ${MAX_DESCRIPTION_LENGTH} characters)`
            }
            sx={fieldSx}
          />

          {/* Logo upload */}
          <Box sx={{ mb: 3 }}>
            <InputLabel
              shrink
              htmlFor="logo-upload"
              sx={{ color: T.muted, fontWeight: 500, mb: 1 }}
            >
              Company Logo * (Max {MAX_FILE_SIZE_MB} MB) &amp; (MAX 5000x5000px)
            </InputLabel>
            <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                border: `1px dashed ${T.surfaceAlt}`,
                backgroundColor: T.surface,
              }}
            >
              <input
                id="logo-upload"
                type="file"
                name="logo"
                required
                accept="image/*"
                onChange={(e) => handleChange(e, 1)}
                style={{ width: "100%" }}
              />
              {userDetails.logoPreview && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: T.muted }}>
                    Preview:
                  </Typography>
                  <img
                    src={userDetails.logoPreview}
                    alt="Logo Preview"
                    style={{
                      display: "block",
                      maxWidth: "150px",
                      maxHeight: "150px",
                      marginTop: 4,
                      borderRadius: "8px",
                      border: `1px solid ${T.surfaceAlt}`,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleBack}
              sx={{
                color: T.dark,
                borderColor: T.surfaceAlt,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                padding: "11px 24px",
                "&:hover": { borderColor: T.dark, backgroundColor: T.surface },
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit}
              sx={primaryButtonStyles}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Complete Registration"
              )}
            </Button>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        fontFamily:
          "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        background: `linear-gradient(135deg, ${T.dark} 0%, #1a2b3c 40%, #0d1f30 100%)`,
        position: "relative",
        overflow: "hidden",
        py: 8,
      }}
    >
      <ToastContainer />
      <SiteNavbar />

      {/* Geometric background decoration */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -180,
            right: -180,
            width: 560,
            height: 560,
            borderRadius: "50%",
            border: "1px solid rgba(209,216,224,.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 360,
            height: 360,
            borderRadius: "50%",
            border: "1px solid rgba(209,216,224,.12)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 600,
            height: 600,
            borderRadius: "50%",
            border: "1px solid rgba(209,216,224,.06)",
          }}
        />
      </Box>

      {/* Brand mark */}
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          justifyContent: "center",
          mb: 3,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            background: T.chipText,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 17, fontWeight: 900, color: T.dark }}>
            S
          </span>
        </Box>
        <span
          style={{
            fontSize: 19,
            fontWeight: 800,
            color: T.white,
            letterSpacing: -0.5,
          }}
        >
          Spade ERP
        </span>
      </Box> */}

      {/* Card */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 560,
          mx: "auto",
          padding: 4,
          borderRadius: "18px",
          boxShadow: "0 24px 60px rgba(0,0,0,.35)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          align="center"
          sx={{
            mb: 1,
            fontWeight: 800,
            letterSpacing: "-0.5px",
            color: T.dark,
          }}
        >
          Enterprise Registration
        </Typography>
        <Typography
          align="center"
          sx={{ mb: 4, fontSize: 13.5, color: T.muted, lineHeight: 1.6 }}
        >
          Set up your workspace in two quick steps
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: T.border,
                    "&.Mui-active": { color: T.dark },
                    "&.Mui-completed": { color: T.dark },
                    "& .MuiStepIcon-text": { fill: T.white, fontWeight: 700 },
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: activeStep === index ? 700 : 500,
                    color: activeStep === index ? T.dark : T.muted,
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={(e) => e.preventDefault()}>{renderStepContent()}</form>
      </Paper>

      {/* Login link */}
      <Box sx={{ mt: 3, textAlign: "center", position: "relative", zIndex: 1 }}>
        <Typography variant="body1" sx={{ color: T.chipText }}>
          Already have an account?{" "}
          <Button
            variant="text"
            onClick={() => navigate("/login")}
            sx={{
              color: T.white,
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Log In
          </Button>
        </Typography>
      </Box>
    </Box>
  );
};

export default Register;
